import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { analyticsCore } from './core';
import { SystemTypedEvents, EventCategory } from './types';

export interface AnalyticsContextType {
  trackEvent: <T extends SystemTypedEvents>(
    name: T['name'],
    properties: T['props'],
    category: EventCategory
  ) => void;
  setUser: (userId: string | null) => void;
  setAnonymize: (value: boolean) => void;
  flushEvents: () => Promise<void>;
}

const AnalyticsContext = createContext<AnalyticsContextType>({
  trackEvent: () => {},
  setUser: () => {},
  setAnonymize: () => {},
  flushEvents: async () => {},
});

export const useAnalytics = () => useContext(AnalyticsContext);

export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Use a memoized stable model to guarantee sub-renders never trigger on callback comparisons
  const value = useMemo<AnalyticsContextType>(() => ({
    trackEvent: <T extends SystemTypedEvents>(
      name: T['name'],
      properties: T['props'],
      category: EventCategory
    ) => {
      analyticsCore.track({
        name,
        props: properties,
        category,
      });
    },
    setUser: (userId) => {
      analyticsCore.setUserId(userId);
    },
    setAnonymize: (value) => {
      analyticsCore.setAnonymizeLogging(value);
    },
    flushEvents: async () => {
      await analyticsCore.flush();
    },
  }), []);

  // Shutdown flush upon unmount to secure any pending events in the queue
  useEffect(() => {
    return () => {
      analyticsCore.flush();
      analyticsCore.shutdown();
    };
  }, []);

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};
